import { z } from 'zod'

import {
  PAYMENT_STATUSES,
  REGISTRATION_STATUSES,
  VERIFICATION_STATUSES,
  type RegistrationFilters,
  type RegistrationSummary,
} from '#admin/features/registrations/api.ts'

/** Only a value from the axis's own vocabulary survives; anything else in the URL means "any". */
function oneOf<T extends string>(values: readonly T[], value: unknown): T | undefined {
  return values.find((candidate) => candidate === value)
}

export type Search = {
  q?: string | undefined
  status?: RegistrationSummary['status'] | undefined
  paymentStatus?: RegistrationSummary['paymentStatus'] | undefined
  verificationStatus?: RegistrationSummary['verificationStatus'] | undefined
  ticketTypeId?: string | undefined
}

/**
 * Every filter lives in the URL, so "paid but unverified" is a link an operator can paste to a
 * colleague, and Back returns to the same pile. All optional, so a plain link to the list needs
 * no search at all.
 *
 * Every key is returned, `undefined` when dropped: the router spreads this over the raw URL
 * search, so a key left out would keep its raw value and reach the API anyway.
 */
export function parseSearch(search: Record<string, unknown>): Search {
  const q = typeof search['q'] === 'string' ? search['q'].trim().slice(0, 200) : ''
  return {
    q: q === '' ? undefined : q,
    // A bookmark from before the US spelling change can say `cancelled`. Keep it working.
    status: oneOf(
      REGISTRATION_STATUSES,
      search['status'] === 'cancelled' ? 'canceled' : search['status'],
    ),
    paymentStatus: oneOf(PAYMENT_STATUSES, search['paymentStatus']),
    verificationStatus: oneOf(VERIFICATION_STATUSES, search['verificationStatus']),
    // A uuid or nothing: the API refuses anything else with a 422. A deleted type's id matches nothing.
    ticketTypeId: z.uuid().safeParse(search['ticketTypeId']).data,
  }
}

export function filtersOf(search: Search): RegistrationFilters {
  return {
    q: search.q ?? '',
    status: search.status ?? '',
    paymentStatus: search.paymentStatus ?? '',
    verificationStatus: search.verificationStatus ?? '',
    ticketTypeId: search.ticketTypeId ?? '',
  }
}
